import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeft, ChevronUp, ChevronDown, ChevronsUp, ChevronsDown,
  Plus, Save, Trash2, Check, Loader2, AlertCircle,
  Type, Square, Image, LayoutGrid, Clock, AlignLeft, Trophy,
  Eye, EyeOff, Copy, ExternalLink, FolderOpen, GripVertical,
  Undo2, Redo2, Download,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import DesktopOnlyNotice from '@/components/DesktopOnlyNotice';
import { overlaysApi, assetsApi, slideshowApi, queryKeys, OverlayComposition, OverlayWidget, Slideshow } from '@/lib/api';
import { sortByNatural } from '@/lib/utils';
import { SchemeEditorModal } from '@/components/SchemeEditorModal';

// ─── Overlay Variables ────────────────────────────────────────────────────────

export const OVERLAY_VARIABLES = [
  { key: 'sheetId',     label: 'Sheet ID',      description: 'e.g. A, B, C' },
  { key: 'overlayName', label: 'Overlay Name',   description: 'The per-sheet overlay name (preserved per sheet even when an Event Overlay is live)' },
  { key: 'eventName',   label: 'Event Name',     description: 'The Event Name typed on the overlay card' },
  { key: 'facilityId',  label: 'Facility ID',    description: 'The facility identifier' },
  // From the schedule for this sheet — the game actually on air. Blank between
  // draws, so a bound lower third disappears rather than showing stale teams.
  { key: 'team1',       label: 'Team 1',         description: 'From the schedule — home team of the game on this sheet' },
  { key: 'team2',       label: 'Team 2',         description: 'From the schedule — away team of the game on this sheet' },
  { key: 'matchup',     label: 'Team 1 vs Team 2', description: 'From the schedule — both teams on one line; prints once if the source gives the same name twice' },
  { key: 'gameName',    label: 'League / Game',  description: 'From the schedule — the league or event name of the game on this sheet' },
  { key: 'drawLabel',   label: 'Draw Label',     description: 'From the schedule — the draw label, when the game has one' },
] as const;

/** Variables that come from the schedule and are blank when no game is on. */
export const SCHEDULE_VARIABLE_KEYS: readonly string[] = ['team1', 'team2', 'matchup', 'gameName', 'drawLabel'];

export type OverlayVarKey = typeof OVERLAY_VARIABLES[number]['key'];

export interface OverlayVarCtx {
  sheetId: string;
  overlayName: string;
  eventName: string;
  facilityId: string;
  /** Live schedule values, when the editor knows them. Blank/absent otherwise. */
  team1?: string;
  team2?: string;
  matchup?: string;
  gameName?: string;
  drawLabel?: string;
}

/**
 * Both team names on one line. Kept in step with the server's {matchup} rule:
 * never "TBD", and a single name when the source repeats it (some schedules copy
 * the league name into both team columns when the real teams are unknown).
 */
export function buildMatchup(team1?: string, team2?: string): string {
  const t1 = (team1 ?? '').trim();
  const t2 = (team2 ?? '').trim();
  if (t1 && t1.localeCompare(t2, undefined, { sensitivity: 'accent' }) === 0) return t1;
  if (t1 && t2) return `${t1} vs ${t2}`;
  return t1 || t2;
}

export function resolveOverlayVar(key: string, ctx: OverlayVarCtx): string {
  if (key === 'sheetId')     return ctx.sheetId;
  if (key === 'overlayName') return ctx.overlayName;
  if (key === 'eventName')   return ctx.eventName;
  if (key === 'facilityId')  return ctx.facilityId;
  if (key === 'team1')       return ctx.team1 ?? '';
  if (key === 'team2')       return ctx.team2 ?? '';
  if (key === 'matchup')     return ctx.matchup ?? buildMatchup(ctx.team1, ctx.team2);
  if (key === 'gameName')    return ctx.gameName ?? '';
  if (key === 'drawLabel')   return ctx.drawLabel ?? '';
  return `{{${key}}}`;
}

// ─── CSS Utility Functions ────────────────────────────────────────────────────
interface GradientStop { color: string; position: number; }

function resolveBg(p: any, prefix: string): string {
  const type = (p[`${prefix}Type`] as string) || 'solid';
  const color = (p[`${prefix}Color`] as string) || (prefix === 'fill' ? '#2563eb' : 'transparent');
  const angle = (p[`${prefix}Angle`] as number) ?? 90;
  const stops: GradientStop[] = (p[`${prefix}Stops`] as GradientStop[]) || [{ color: '#000000', position: 0 }, { color: '#ffffff', position: 100 }];
  if (type === 'none') return 'transparent';
  if (type === 'solid') return color;
  const css = stops.map(s => `${s.color} ${s.position}%`).join(', ');
  if (type === 'linear') return `linear-gradient(${angle}deg, ${css})`;
  return `radial-gradient(circle, ${css})`;
}

function resolveBoxShadow(p: any): string | undefined {
  if (!p.shadowEnabled) return undefined;
  return `${p.shadowInset ? 'inset ' : ''}${p.shadowX ?? 0}px ${p.shadowY ?? 4}px ${p.shadowBlur ?? 8}px ${p.shadowSpread ?? 0}px ${p.shadowColor ?? 'rgba(0,0,0,0.5)'}`;
}

function resolveFilter(p: any): string | undefined {
  const parts: string[] = [];
  if (p.filterBlur) parts.push(`blur(${p.filterBlur}px)`);
  if (p.filterBrightness != null && p.filterBrightness !== 100) parts.push(`brightness(${p.filterBrightness}%)`);
  if (p.filterContrast != null && p.filterContrast !== 100) parts.push(`contrast(${p.filterContrast}%)`);
  if (p.filterSaturate != null && p.filterSaturate !== 100) parts.push(`saturate(${p.filterSaturate}%)`);
  if (p.filterGrayscale) parts.push(`grayscale(${p.filterGrayscale}%)`);
  return parts.length ? parts.join(' ') : undefined;
}

function resolveTextShadow(p: any): string | undefined {
  if (!p.textShadowEnabled) return undefined;
  return `${p.textShadowX ?? 2}px ${p.textShadowY ?? 2}px ${p.textShadowBlur ?? 4}px ${p.textShadowColor ?? 'rgba(0,0,0,0.8)'}`;
}

// ─── Reusable Inspector Sub-components ────────────────────────────────────────
function Section({ title, children, defaultOpen = true }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-gray-100 dark:border-gray-800 first:border-t-0">
      <button onClick={() => setOpen(v => !v)}
        className="flex w-full items-center justify-between px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
        {title}
        <span className={`text-gray-400 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}>▾</span>
      </button>
      {open && <div className="space-y-2.5 px-4 pb-3">{children}</div>}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-0.5 block text-xs text-gray-500 dark:text-gray-400">{label}</label>
      {children}
    </div>
  );
}

const INPUT = 'w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';
const COLOR = 'h-8 w-full cursor-pointer rounded border border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800';
const SELECT = INPUT;

function BackgroundPicker({ prefix, p, setProp, allowNone = true, label = 'Background' }: {
  prefix: string; p: any;
  setProp: (k: string, v: unknown) => void;
  allowNone?: boolean; label?: string;
}) {
  const type = (p[`${prefix}Type`] as string) || 'solid';
  const color = (p[`${prefix}Color`] as string) || '#000000';
  const angle = (p[`${prefix}Angle`] as number) ?? 90;
  const stops: GradientStop[] = (p[`${prefix}Stops`] as GradientStop[]) || [{ color: '#000000', position: 0 }, { color: '#ffffff', position: 100 }];
  const types = allowNone ? ['none', 'solid', 'linear', 'radial'] : ['solid', 'linear', 'radial'];
  const previewBg = resolveBg(p, prefix);

  return (
    <div className="space-y-2">
      <label className="block text-xs text-gray-500 dark:text-gray-400">{label}</label>
      <div className="flex overflow-hidden rounded border border-gray-300 text-xs dark:border-gray-600">
        {types.map(t => (
          <button key={t} onClick={() => setProp(`${prefix}Type`, t)}
            className={`flex-1 py-1 capitalize transition-colors ${type === t ? 'bg-blue-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'}`}>
            {t}
          </button>
        ))}
      </div>
      {type === 'solid' && (
        <input type="color" value={color} onChange={e => setProp(`${prefix}Color`, e.target.value)} className={COLOR} />
      )}
      {(type === 'linear' || type === 'radial') && (
        <div className="space-y-2">
          <div className="h-6 w-full rounded border border-gray-300 dark:border-gray-600" style={{ background: previewBg }} />
          {type === 'linear' && (
            <Row label="Angle °">
              <input type="number" min={0} max={360} value={angle} onChange={e => setProp(`${prefix}Angle`, Number(e.target.value))} className={INPUT} />
            </Row>
          )}
          <label className="block text-xs text-gray-500 dark:text-gray-400">Color Stops</label>
          {stops.map((stop, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <input type="color" value={stop.color}
                onChange={e => setProp(`${prefix}Stops`, stops.map((s, j) => j === i ? { ...s, color: e.target.value } : s))}
                className="h-7 w-8 shrink-0 cursor-pointer rounded border border-gray-300 dark:border-gray-600" />
              <input type="number" min={0} max={100} value={stop.position}
                onChange={e => setProp(`${prefix}Stops`, stops.map((s, j) => j === i ? { ...s, position: Number(e.target.value) } : s))}
                className="w-14 rounded border border-gray-300 bg-white px-1.5 py-0.5 text-xs text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" />
              <span className="text-xs text-gray-400">%</span>
              {stops.length > 2 && (
                <button onClick={() => setProp(`${prefix}Stops`, stops.filter((_, j) => j !== i))}
                  className="ml-auto text-xs text-red-400 hover:text-red-600">✕</button>
              )}
            </div>
          ))}
          <button onClick={() => setProp(`${prefix}Stops`, [...stops, { color: '#ffffff', position: 100 }])}
            className="text-xs text-blue-500 hover:text-blue-700">+ Add stop</button>
        </div>
      )}
    </div>
  );
}

function ShadowEditor({ p, setProp, prefix = 'shadow', label = 'Box Shadow' }: {
  p: any; setProp: (k: string, v: unknown) => void; prefix?: string; label?: string;
}) {
  const enabled = !!p[`${prefix}Enabled`];
  return (
    <div className="space-y-2">
      <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        <input type="checkbox" checked={enabled} onChange={e => setProp(`${prefix}Enabled`, e.target.checked)} className="h-3 w-3" />
        {label}
      </label>
      {enabled && (
        <div className="space-y-2 pl-3">
          <div className="grid grid-cols-2 gap-2">
            {[['X', 0], ['Y', 4], ['Blur', 8], ['Spread', 0]] .map(([f, def]) => (
              <Row key={f as string} label={`${f} (px)`}>
                <input type="number" value={(p[`${prefix}${f}`] as number) ?? def}
                  onChange={e => setProp(`${prefix}${f}`, Number(e.target.value))}
                  className="w-full rounded border border-gray-300 bg-white px-2 py-0.5 text-xs text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" />
              </Row>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Row label="Color">
              <input type="color" value={(p[`${prefix}Color`] as string) || '#000000'}
                onChange={e => setProp(`${prefix}Color`, e.target.value)}
                className="h-7 w-full cursor-pointer rounded border border-gray-300 dark:border-gray-600" />
            </Row>
            <div className="flex items-end pb-0.5">
              <label className="flex cursor-pointer items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                <input type="checkbox" checked={!!p[`${prefix}Inset`]} onChange={e => setProp(`${prefix}Inset`, e.target.checked)} className="h-3 w-3" />
                Inset
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterEditor({ p, setProp }: { p: any; setProp: (k: string, v: unknown) => void }) {
  return (
    <div className="space-y-3">
      {[
        { label: 'Blur (px)', key: 'filterBlur', min: 0, max: 40, def: 0 },
        { label: 'Brightness (%)', key: 'filterBrightness', min: 0, max: 200, def: 100 },
        { label: 'Contrast (%)', key: 'filterContrast', min: 0, max: 200, def: 100 },
        { label: 'Saturate (%)', key: 'filterSaturate', min: 0, max: 200, def: 100 },
        { label: 'Grayscale (%)', key: 'filterGrayscale', min: 0, max: 100, def: 0 },
      ].map(({ label, key, min, max, def }) => (
        <div key={key}>
          <div className="mb-0.5 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>{label}</span><span>{(p[key] as number) ?? def}</span>
          </div>
          <input type="range" min={min} max={max} value={(p[key] as number) ?? def}
            onChange={e => setProp(key, Number(e.target.value))}
            className="h-1.5 w-full accent-blue-500" />
        </div>
      ))}
      <Row label="Blend Mode">
        <select value={(p.mixBlendMode as string) || 'normal'} onChange={e => setProp('mixBlendMode', e.target.value)} className={SELECT}>
          {['normal','multiply','screen','overlay','darken','lighten','color-dodge','color-burn','hard-light','soft-light','difference','exclusion','hue','saturation','color','luminosity'].map(m => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </Row>
    </div>
  );
}

// ─── Constants ────────────────────────────────────────────────────────────────
const CANVAS_W = 1920;
const CANVAS_H = 1080;

const WIDGET_TYPES: Array<{ type: OverlayWidget['type']; label: string; icon: React.ComponentType<any> }> = [
  { type: 'text',       label: 'Text',       icon: Type },
  { type: 'shape',      label: 'Shape',      icon: Square },
  { type: 'image',      label: 'Image',      icon: Image },
  { type: 'slideshow',  label: 'Slideshow',  icon: LayoutGrid },
  { type: 'clock',      label: 'Clock',      icon: Clock },
  { type: 'ticker',     label: 'Ticker',     icon: AlignLeft },
  { type: 'scoreboard', label: 'Scoreboard', icon: Trophy },
];

type Handle = 'nw'|'n'|'ne'|'e'|'se'|'s'|'sw'|'w';
const HANDLES: Handle[] = ['nw','n','ne','e','se','s','sw','w'];
const HANDLE_STYLE: Record<Handle, React.CSSProperties> = {
  nw: { top:-4, left:-4, cursor:'nw-resize' },
  n:  { top:-4, left:'50%', transform:'translateX(-50%)', cursor:'n-resize' },
  ne: { top:-4, right:-4, cursor:'ne-resize' },
  e:  { top:'50%', right:-4, transform:'translateY(-50%)', cursor:'e-resize' },
  se: { bottom:-4, right:-4, cursor:'se-resize' },
  s:  { bottom:-4, left:'50%', transform:'translateX(-50%)', cursor:'s-resize' },
  sw: { bottom:-4, left:-4, cursor:'sw-resize' },
  w:  { top:'50%', left:-4, transform:'translateY(-50%)', cursor:'w-resize' },
};

function defaultWidget(type: OverlayWidget['type']): Omit<OverlayWidget, 'id'|'sceneId'|'createdAt'|'updatedAt'> {
  const base = { type, name: type.charAt(0).toUpperCase() + type.slice(1), x:100, y:100, width:400, height:100, rotation:0, opacity:1, zIndex:1, isVisible:true, isLocked:false, assetIds:[] };
  const p: Record<string, unknown> = {};
  switch (type) {
    case 'text':       Object.assign(p, { content:'Text', fontSize:48, fontFamily:'Arial', fontWeight:400, color:'#ffffff', bgType:'none', bgColor:'#000000', letterSpacing:0, lineHeight:1.2, textAlign:'left', padding:8, textTransform:'none', textDecoration:'none' }); break;
    case 'shape':      Object.assign(p, { shapeType:'rectangle', fillType:'solid', fillColor:'#2563eb', borderColor:'#1d4ed8', borderWidth:0, borderRadius:0, borderStyle:'solid' }); break;
    case 'image':      Object.assign(p, { objectFit:'contain' }); break;
    case 'slideshow':  Object.assign(p, { objectFit:'contain', transitionType:'fade', transitionDuration:0.8 }); break;
    case 'clock':      Object.assign(p, { format:'HH:mm:ss', fontSize:64, fontFamily:'monospace', fontWeight:400, color:'#ffffff', bgType:'none', bgColor:'#000000', letterSpacing:0 }); break;
    case 'ticker':     Object.assign(p, { content:'Breaking news ticker...', fontSize:32, color:'#ffffff', bgType:'solid', bgColor:'#1d4ed8', scrollSpeed:80, direction:'left' }); break;
    case 'scoreboard': Object.assign(p, { team1:'Team 1', score:'0 - 0', team2:'Team 2', fontSize:48, colorTeam1:'#ffffff', colorTeam2:'#ffffff', colorScore:'#ffdd00', bgType:'solid', bgColor:'#1e3a5f', showHammer:false, hammerTeam:'1' }); break;
  }
  return { ...base, properties: p };
}

// ─── Widget Canvas Renderer ───────────────────────────────────────────────────
function WidgetPreview({ widget, isSelected, assetMap, slideshowPreviewUrl, varCtx, onPointerDown, onResizeStart }: {
  widget: OverlayWidget; isSelected: boolean;
  assetMap?: Record<number, { s3Url: string; signedUrl?: string; thumbnailUrl?: string }>;
  slideshowPreviewUrl?: string;
  varCtx?: OverlayVarCtx;
  onPointerDown: (e: React.PointerEvent) => void;
  onResizeStart: (e: React.PointerEvent, h: Handle) => void;
}) {
  if (!widget.isVisible) return null;
  const p: any = widget.properties;

  const boxShadow = resolveBoxShadow(p);
  const filter = resolveFilter(p);

  const style: React.CSSProperties = {
    position:'absolute', left:widget.x, top:widget.y, width:widget.width, height:widget.height,
    transform:`rotate(${widget.rotation}deg)`, opacity:widget.opacity, zIndex:widget.zIndex,
    cursor:widget.isLocked ? 'default' : 'move', boxSizing:'border-box', overflow:'hidden',
    outline: isSelected ? '2px solid #3b82f6' : '1px solid transparent', outlineOffset:'1px',
    boxShadow, filter,
    ...(p.mixBlendMode && p.mixBlendMode !== 'normal' ? { mixBlendMode: p.mixBlendMode as any } : {}),
  };

  let inner: React.ReactNode = null;

  switch (widget.type) {
    case 'text': {
      const bg = p.bgType ? resolveBg(p, 'bg') : (p.backgroundColor || 'transparent');
      style.background = bg; style.padding = `${p.padding || 8}px`;
      const vAlignMap: Record<string, string> = { top: 'flex-start', middle: 'center', bottom: 'flex-end' };
      style.display = 'flex'; style.alignItems = vAlignMap[p.verticalAlign as string] ?? 'center';
      inner = (
        <span style={{
          fontSize:`${p.fontSize || 48}px`, fontFamily:(p.fontFamily as string) || 'Arial',
          fontWeight:(p.fontWeight as number) || (p.bold ? 700 : 400),
          color:(p.color as string) || '#fff',
          fontStyle: p.italic ? 'italic' : 'normal',
          textAlign:(p.textAlign as any) || 'left', width:'100%',
          lineHeight: p.lineHeight || 1.2,
          letterSpacing: p.letterSpacing ? `${p.letterSpacing}px` : undefined,
          textDecoration:(p.textDecoration as string) || 'none',
          textTransform:(p.textTransform as any) || 'none',
          textShadow: resolveTextShadow(p),
          WebkitTextStroke: p.textStrokeWidth ? `${p.textStrokeWidth}px ${p.textStrokeColor || '#000'}` : undefined,
        }}>
          {p.variableMode && p.variableKey && varCtx
            ? resolveOverlayVar(p.variableKey as string, varCtx)
            : (p.content as string) || 'Text'}
        </span>
      );
      break;
    }
    case 'shape': {
      const bg = p.fillType ? resolveBg(p, 'fill') : (p.fillColor || '#2563eb');
      if (bg === 'transparent') {
        // Transparent fill — show checkerboard in editor so the area is clearly visible
        style.backgroundColor = '#888';
        style.backgroundImage = 'linear-gradient(45deg,#666 25%,transparent 25%),linear-gradient(-45deg,#666 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#666 75%),linear-gradient(-45deg,transparent 75%,#666 75%)';
        style.backgroundSize = '24px 24px';
        style.backgroundPosition = '0 0,0 12px,12px -12px,-12px 0';
      } else {
        style.background = bg;
      }
      style.border = p.borderWidth ? `${p.borderWidth}px ${p.borderStyle || 'solid'} ${p.borderColor || '#1d4ed8'}` : undefined;
      style.borderRadius = p.shapeType === 'ellipse' ? '50%' : `${p.borderRadius || 0}px`;
      break;
    }
    case 'image': {
      const firstAsset = widget.assetIds?.[0] != null ? assetMap?.[widget.assetIds[0]] : undefined;
      const firstAssetUrl = firstAsset?.signedUrl ?? firstAsset?.s3Url;
      inner = firstAssetUrl ? (
        <img src={firstAssetUrl} alt="" style={{ width:'100%', height:'100%', objectFit:(p.objectFit as any) || 'contain' }} />
      ) : (
        <div style={{ width:'100%', height:'100%', background:'#1f2937', display:'flex', alignItems:'center', justifyContent:'center' }}>
          <Image className="h-8 w-8 text-gray-500" />
        </div>
      );
      break;
    }
    case 'slideshow': {
      const firstSlideUrl = slideshowPreviewUrl;
      inner = firstSlideUrl ? (
        <img src={firstSlideUrl} alt="" style={{ width:'100%', height:'100%', objectFit:(p.objectFit as any) || 'contain' }} />
      ) : (
        <div style={{ width:'100%', height:'100%', background:'#1f2937', display:'flex', alignItems:'center', justifyContent:'center' }}>
          <LayoutGrid className="h-8 w-8 text-gray-500" />
        </div>
      );
      break;
    }
    case 'clock': {
      const bg = p.bgType ? resolveBg(p, 'bg') : (p.backgroundColor || 'transparent');
      style.background = bg;
      inner = (
        <div style={{ fontSize:`${p.fontSize || 64}px`, color:(p.color as string) || '#fff', fontFamily:(p.fontFamily as string) || 'monospace', fontWeight:(p.fontWeight as number) || 400, letterSpacing: p.letterSpacing ? `${p.letterSpacing}px` : undefined, textAlign:(p.textAlign as any) || 'center', width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent: p.textAlign === 'left' ? 'flex-start' : p.textAlign === 'right' ? 'flex-end' : 'center' }}>
          {applyClockFormat((p.format as string) || 'HH:mm:ss')}
        </div>
      );
      break;
    }
    case 'ticker': {
      const bg = p.bgType ? resolveBg(p, 'bg') : (p.backgroundColor || '#1d4ed8');
      style.background = bg;
      inner = (
        <div style={{ fontSize:`${p.fontSize || 32}px`, color:(p.color as string) || '#fff', padding:'0 16px', width:'100%', height:'100%', display:'flex', alignItems:'center', overflow:'hidden' }}>
          {(p.content as string) || 'Ticker'}
        </div>
      );
      break;
    }
    case 'scoreboard': {
      const bg = p.bgType ? resolveBg(p, 'bg') : (p.backgroundColor || '#1e3a5f');
      style.background = bg;
      inner = (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%', height:'100%', padding:'0 24px', fontSize:`${p.fontSize || 48}px` }}>
          <span style={{ color:(p.colorTeam1 as string) || '#fff' }}>
            {(p.team1 as string) || 'Team 1'}
            {p.showHammer && p.hammerTeam === '1' && <span style={{ fontSize:'0.5em', verticalAlign:'super', marginLeft:4 }}>🪨</span>}
          </span>
          <span style={{ color:(p.colorScore as string) || '#ffdd00', fontWeight:'bold' }}>{(p.score as string) || '0 - 0'}</span>
          <span style={{ color:(p.colorTeam2 as string) || '#fff' }}>
            {p.showHammer && p.hammerTeam === '2' && <span style={{ fontSize:'0.5em', verticalAlign:'super', marginRight:4 }}>🪨</span>}
            {(p.team2 as string) || 'Team 2'}
          </span>
        </div>
      );
      break;
    }
  }

  return (
    <div style={style} onPointerDown={widget.isLocked ? undefined : onPointerDown}>
      {inner}
      {isSelected && !widget.isLocked && HANDLES.map(h => (
        <div key={h} onPointerDown={e => { e.stopPropagation(); onResizeStart(e, h); }}
          style={{ position:'absolute', width:8, height:8, background:'#fff', border:'2px solid #3b82f6', borderRadius:2, boxSizing:'border-box', zIndex:9999, ...HANDLE_STYLE[h] }} />
      ))}
    </div>
  );
}

// ─── Inspector Panel ──────────────────────────────────────────────────────────
const FONT_OPTIONS = [
  'Arial', 'Arial Black', 'Arial Narrow', 'Helvetica',
  'Verdana', 'Tahoma', 'Trebuchet MS', 'Impact',
  'Georgia', 'Times New Roman', 'Palatino', 'Garamond',
  'Courier New', 'Lucida Console',
  'sans-serif', 'serif', 'monospace',
];

function applyClockFormat(fmt: string): string {
  const now = new Date();
  return fmt
    .replace('YYYY', String(now.getFullYear()))
    .replace('YY',   String(now.getFullYear()).slice(-2))
    .replace('MM',   String(now.getMonth() + 1).padStart(2, '0'))
    .replace('DD',   String(now.getDate()).padStart(2, '0'))
    .replace('HH',   String(now.getHours()).padStart(2, '0'))
    .replace('hh',   String(now.getHours() % 12 || 12).padStart(2, '0'))
    .replace('mm',   String(now.getMinutes()).padStart(2, '0'))
    .replace('ss',   String(now.getSeconds()).padStart(2, '0'));
}

// ─── Inline Asset Picker ─────────────────────────────────────────────────────
type AssetItem = { id: number; originalName: string; s3Url: string; signedUrl?: string; thumbnailUrl?: string };

function AssetPickerSection({ widgetType, assets, selectedIds, onSelect }: {
  widgetType: 'image' | 'slideshow';
  assets: AssetItem[];
  selectedIds: number[];
  onSelect: (ids: number[]) => void;
}) {
  const toggle = (id: number) => {
    if (widgetType === 'image') {
      onSelect(selectedIds[0] === id ? [] : [id]);
    } else {
      onSelect(selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id]);
    }
  };

  return (
    <div>
      {/* Header row */}
      <div className="mb-1.5 flex items-center justify-between">
        <label className="text-xs text-gray-500 dark:text-gray-400">
          {widgetType === 'image' ? 'Asset' : `Assets (${selectedIds.length} selected)`}
        </label>
        {widgetType === 'slideshow' && (
          <a href="/slideshows" target="_blank" rel="noreferrer"
            className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20">
            <FolderOpen className="h-3 w-3" />Manage
          </a>
        )}
        {widgetType === 'image' && (
          <a href="/logos" target="_blank" rel="noreferrer"
            className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20">
            <FolderOpen className="h-3 w-3" />Manage logos
          </a>
        )}
      </div>

      {/* Asset grid */}
      <div className="max-h-56 overflow-y-auto rounded border border-gray-200 dark:border-gray-700">
        {assets.length === 0 ? (
          <div className="p-3 text-center">
            <p className="text-xs text-gray-400">No logos uploaded yet.</p>
            <p className="mt-1 text-[10px] text-gray-400">Upload logos in the Logos section.</p>
          </div>
        ) : widgetType === 'image' ? (
          /* Image: 3-column thumbnail grid */
          <div className="grid grid-cols-3 gap-1 p-1.5">
            {assets.map(a => {
              const sel = selectedIds[0] === a.id;
              return (
                <button key={a.id} title={a.originalName} onClick={() => toggle(a.id)}
                  className={`group relative aspect-square overflow-hidden rounded border-2 transition-colors ${sel ? 'border-blue-500' : 'border-transparent hover:border-blue-300'}`}>
                  <img src={a.signedUrl ?? a.thumbnailUrl ?? a.s3Url} alt={a.originalName}
                    className="h-full w-full object-contain bg-gray-100 dark:bg-gray-800" />
                  {sel && (
                    <div className="absolute inset-0 flex items-center justify-center bg-blue-500/20">
                      <Check className="h-4 w-4 text-blue-600 drop-shadow" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        ) : (
          /* Slideshow: list with thumbnail + name */
          assets.map(a => {
            const sel = selectedIds.includes(a.id);
            return (
              <div key={a.id} onClick={() => toggle(a.id)}
                className={`flex cursor-pointer items-center gap-2 px-2 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 ${sel ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                <div className="flex h-8 w-12 shrink-0 items-center justify-center overflow-hidden rounded bg-gray-100 dark:bg-gray-800">
                  <img src={a.signedUrl ?? a.thumbnailUrl ?? a.s3Url} alt="" className="h-full w-full object-contain" />
                </div>
                <span className="flex-1 truncate text-gray-700 dark:text-gray-300">{a.originalName}</span>
                {sel && <Check className="h-3 w-3 shrink-0 text-blue-500" />}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function InspectorPanel({ widget, onChange, assets, slideshows, varCtx,
  onBringToFront, onSendToBack, onBringForward, onSendBackward,
}: {
  widget: OverlayWidget;
  onChange: (updated: OverlayWidget) => void;
  assets: AssetItem[];
  slideshows: Slideshow[];
  varCtx?: OverlayVarCtx;
  onBringToFront?: () => void;
  onSendToBack?: () => void;
  onBringForward?: () => void;
  onSendBackward?: () => void;
}) {
  const set = (field: keyof OverlayWidget, value: unknown) => onChange({ ...widget, [field]: value });
  const setProp = (key: string, value: unknown) => onChange({ ...widget, properties: { ...widget.properties, [key]: value } });
  const setProps = (updates: Record<string, unknown>) => onChange({ ...widget, properties: { ...widget.properties, ...updates } });
  const p: any = widget.properties;

  return (
    <div className="flex flex-col overflow-y-auto text-sm">

      {/* Transform */}
      <Section title="Transform">
        <div className="grid grid-cols-2 gap-2">
          {(['x','y','width','height'] as const).map(f => (
            <Row key={f} label={f.toUpperCase()}>
              <input type="number" value={Math.round(widget[f])} onChange={e => set(f, Number(e.target.value))} className={INPUT} />
            </Row>
          ))}
          <Row label="Rotation °">
            <input type="number" value={widget.rotation} onChange={e => set('rotation', Number(e.target.value))} className={INPUT} />
          </Row>
          <Row label="Layer">
            <div className="flex items-center gap-1">
              <button title="Send to back" onClick={onSendToBack} className="rounded border border-gray-300 p-0.5 hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700"><ChevronsDown className="h-3.5 w-3.5" /></button>
              <button title="Send backward" onClick={onSendBackward} className="rounded border border-gray-300 p-0.5 hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700"><ChevronDown className="h-3.5 w-3.5" /></button>
              <button title="Bring forward" onClick={onBringForward} className="rounded border border-gray-300 p-0.5 hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700"><ChevronUp className="h-3.5 w-3.5" /></button>
              <button title="Bring to front" onClick={onBringToFront} className="rounded border border-gray-300 p-0.5 hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700"><ChevronsUp className="h-3.5 w-3.5" /></button>
              <span className="ml-1 text-[10px] text-gray-400 dark:text-gray-500">z:{widget.zIndex}</span>
            </div>
          </Row>
          <Row label="Opacity">
            <input type="number" step="0.05" min="0" max="1" value={widget.opacity} onChange={e => set('opacity', Number(e.target.value))} className={INPUT} />
          </Row>
        </div>
        <div className="flex gap-4 pt-1">
          <label className="flex cursor-pointer items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
            <input type="checkbox" checked={widget.isVisible} onChange={e => set('isVisible', e.target.checked)} className="h-3.5 w-3.5" /> Visible
          </label>
          <label className="flex cursor-pointer items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
            <input type="checkbox" checked={widget.isLocked} onChange={e => set('isLocked', e.target.checked)} className="h-3.5 w-3.5" /> Locked
          </label>
        </div>
      </Section>

      {/* Widget name */}
      <Section title="Identity">
        <Row label="Name">
          <input type="text" value={widget.name} onChange={e => set('name', e.target.value)} className={INPUT} />
        </Row>
      </Section>

      {/* ── Text ── */}
      {widget.type === 'text' && (<>
        <Section title="Content">
          <label className="mb-2 flex cursor-pointer items-center gap-2 rounded border border-gray-200 px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800/50">
            <input type="checkbox" className="h-3.5 w-3.5 accent-blue-500" checked={!!p.variableMode}
              onChange={e => setProps(e.target.checked
                ? { variableMode: true, variableKey: (p.variableKey as string) || 'sheetId' }
                : { variableMode: false })} />
            <span className="font-medium">Link to Variable</span>
          </label>
          {p.variableMode ? (
            <div className="space-y-2">
              <Row label="Variable">
                <select value={(p.variableKey as string) || 'sheetId'}
                  onChange={e => setProp('variableKey', e.target.value)} className={SELECT}>
                  {OVERLAY_VARIABLES.map(v => (
                    <option key={v.key} value={v.key}>{v.label} — {v.description}</option>
                  ))}
                </select>
              </Row>
              {varCtx && (() => {
                const vKey = (p.variableKey as string) || 'sheetId';
                const resolved = resolveOverlayVar(vKey, varCtx);
                const isScheduleVar = SCHEDULE_VARIABLE_KEYS.includes(vKey);
                return (
                  <div className="rounded bg-blue-50 px-2 py-1.5 dark:bg-blue-900/20">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Preview: </span>
                    {resolved ? (
                      <span className="text-xs font-semibold text-blue-700 dark:text-blue-300">{resolved}</span>
                    ) : isScheduleVar ? (
                      // Be explicit rather than showing an empty box: these are
                      // blank by design until a game is on this sheet.
                      <span className="text-xs italic text-gray-500 dark:text-gray-400">
                        blank until a game is live on this sheet
                      </span>
                    ) : (
                      <span className="text-xs italic text-gray-500 dark:text-gray-400">(empty)</span>
                    )}
                  </div>
                );
              })()}
            </div>
          ) : (
            <Row label="Text">
              <textarea rows={3} value={(p.content as string) || ''} onChange={e => setProp('content', e.target.value)}
                className="w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" />
            </Row>
          )}
          <Row label="Transform">
            <select value={(p.textTransform as string) || 'none'} onChange={e => setProp('textTransform', e.target.value)} className={SELECT}>
              {['none','uppercase','lowercase','capitalize'].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </Row>
          <Row label="Decoration">
            <select value={(p.textDecoration as string) || 'none'} onChange={e => setProp('textDecoration', e.target.value)} className={SELECT}>
              {['none','underline','line-through','overline'].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </Row>
        </Section>

        <Section title="Typography">
          <Row label="Font Family">
            <select value={(p.fontFamily as string) || 'Arial'} onChange={e => setProp('fontFamily', e.target.value)} className={SELECT}>
              {FONT_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </Row>
          <div className="grid grid-cols-2 gap-2">
            <Row label="Size (px)">
              <input type="number" value={(p.fontSize as number) || 48} onChange={e => setProp('fontSize', Number(e.target.value))} className={INPUT} />
            </Row>
            <Row label="Weight">
              <select value={(p.fontWeight as number) || 400} onChange={e => setProp('fontWeight', Number(e.target.value))} className={SELECT}>
                {[100,200,300,400,500,600,700,800,900].map(w => <option key={w} value={w}>{w}</option>)}
              </select>
            </Row>
            <Row label="Letter Spacing (px)">
              <input type="number" step="0.5" value={(p.letterSpacing as number) || 0} onChange={e => setProp('letterSpacing', Number(e.target.value))} className={INPUT} />
            </Row>
            <Row label="Line Height">
              <input type="number" step="0.05" min="0.5" max="4" value={(p.lineHeight as number) || 1.2} onChange={e => setProp('lineHeight', Number(e.target.value))} className={INPUT} />
            </Row>
            <Row label="Padding (px)">
              <input type="number" value={(p.padding as number) || 8} onChange={e => setProp('padding', Number(e.target.value))} className={INPUT} />
            </Row>
            <Row label="H Align">
              <select value={(p.textAlign as string) || 'left'} onChange={e => setProp('textAlign', e.target.value)} className={SELECT}>
                <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option><option value="justify">Justify</option>
              </select>
            </Row>
            <Row label="V Align">
              <select value={(p.verticalAlign as string) || 'middle'} onChange={e => setProp('verticalAlign', e.target.value)} className={SELECT}>
                <option value="top">Top</option><option value="middle">Middle</option><option value="bottom">Bottom</option>
              </select>
            </Row>
          </div>
          <div className="flex gap-4">
            <label className="flex cursor-pointer items-center gap-1 text-xs text-gray-700 dark:text-gray-300"><input type="checkbox" checked={!!p.italic} onChange={e => setProp('italic', e.target.checked)} className="h-3 w-3" /> Italic</label>
          </div>
        </Section>

        <Section title="Colors">
          <Row label="Text Color"><input type="color" value={(p.color as string) || '#ffffff'} onChange={e => setProp('color', e.target.value)} className={COLOR} /></Row>
          <BackgroundPicker prefix="bg" p={p} setProp={setProp} allowNone label="Background" />
        </Section>

        <Section title="Text Shadow" defaultOpen={false}>
          <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <input type="checkbox" checked={!!p.textShadowEnabled} onChange={e => setProp('textShadowEnabled', e.target.checked)} className="h-3 w-3" />
            Enable Text Shadow
          </label>
          {p.textShadowEnabled && (
            <div className="space-y-2 pl-3">
              <div className="grid grid-cols-2 gap-2">
                {[['X (px)', 'textShadowX', 2], ['Y (px)', 'textShadowY', 2], ['Blur (px)', 'textShadowBlur', 4]].map(([lbl, k, def]) => (
                  <Row key={k as string} label={lbl as string}>
                    <input type="number" value={(p[k as string] as number) ?? def} onChange={e => setProp(k as string, Number(e.target.value))}
                      className="w-full rounded border border-gray-300 bg-white px-2 py-0.5 text-xs text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" />
                  </Row>
                ))}
                <Row label="Color">
                  <input type="color" value={(p.textShadowColor as string) || '#000000'} onChange={e => setProp('textShadowColor', e.target.value)}
                    className="h-7 w-full cursor-pointer rounded border border-gray-300 dark:border-gray-600" />
                </Row>
              </div>
            </div>
          )}
        </Section>

        <Section title="Text Stroke" defaultOpen={false}>
          <div className="grid grid-cols-2 gap-2">
            <Row label="Width (px)">
              <input type="number" step="0.5" min="0" value={(p.textStrokeWidth as number) || 0} onChange={e => setProp('textStrokeWidth', Number(e.target.value))} className={INPUT} />
            </Row>
            <Row label="Color">
              <input type="color" value={(p.textStrokeColor as string) || '#000000'} onChange={e => setProp('textStrokeColor', e.target.value)} className={COLOR} />
            </Row>
          </div>
        </Section>
      </>)}

      {/* ── Shape ── */}
      {widget.type === 'shape' && (<>
        <Section title="Shape">
          <Row label="Type">
            <select value={(p.shapeType as string) || 'rectangle'} onChange={e => setProp('shapeType', e.target.value)} className={SELECT}>
              <option value="rectangle">Rectangle</option>
              <option value="ellipse">Ellipse / Circle</option>
            </select>
          </Row>
          {(p.shapeType || 'rectangle') === 'rectangle' && (
            <Row label="Border Radius (px)">
              <input type="number" min="0" value={(p.borderRadius as number) || 0} onChange={e => setProp('borderRadius', Number(e.target.value))} className={INPUT} />
            </Row>
          )}
        </Section>

        <Section title="Fill">
          <BackgroundPicker prefix="fill" p={p} setProp={setProp} allowNone={false} label="Fill" />
        </Section>

        <Section title="Border">
          <div className="grid grid-cols-2 gap-2">
            <Row label="Width (px)">
              <input type="number" min="0" value={(p.borderWidth as number) || 0} onChange={e => setProp('borderWidth', Number(e.target.value))} className={INPUT} />
            </Row>
            <Row label="Style">
              <select value={(p.borderStyle as string) || 'solid'} onChange={e => setProp('borderStyle', e.target.value)} className={SELECT}>
                {['solid','dashed','dotted','double','none'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Row>
            <Row label="Color">
              <input type="color" value={(p.borderColor as string) || '#1d4ed8'} onChange={e => setProp('borderColor', e.target.value)} className={COLOR} />
            </Row>
          </div>
        </Section>
      </>)}

      {/* ── Image / Slideshow ── */}
      {(widget.type === 'image' || widget.type === 'slideshow') && (
        <Section title="Media">
          <Row label="Object Fit">
            <select value={(p.objectFit as string) || 'contain'} onChange={e => setProp('objectFit', e.target.value)} className={SELECT}>
              <option value="contain">Contain</option><option value="cover">Cover</option><option value="fill">Fill</option>
            </select>
          </Row>
          {widget.type === 'slideshow' && (<>
            {/* Per-slide timing moved to the slideshow itself in 1.29.0 — each
                image has its own duration, so there is no widget-wide value. */}
            <p className="text-[11px] text-gray-500 dark:text-gray-400">
              How long each image shows is set per image in its slideshow (Slideshows page).
            </p>
            <Row label="Transition">
              <select value={(p.transitionType as string) || 'fade'} onChange={e => setProp('transitionType', e.target.value)} className={SELECT}>
                <option value="fade">Fade</option>
                <option value="slide-left">Slide Left</option>
                <option value="slide-right">Slide Right</option>
                <option value="slide-up">Slide Up</option>
                <option value="slide-down">Slide Down</option>
                <option value="cut">Cut</option>
              </select>
            </Row>
            {(p.transitionType as string) !== 'cut' && (
              <Row label="Transition Speed (s)">
                <input type="number" min="0.1" max="5" step="0.1" value={(p.transitionDuration as number) ?? 0.8} onChange={e => setProp('transitionDuration', Number(e.target.value))} className={INPUT} />
              </Row>
            )}
            {/* Named slideshow selector */}
            <Row label="Slideshow">
              <select
                value={(p.slideshowId as number | undefined) ?? ''}
                onChange={e => setProp('slideshowId', e.target.value ? Number(e.target.value) : null)}
                className={SELECT}
              >
                <option value="">Facility default</option>
                {slideshows.map(sw => (
                  <option key={sw.id} value={sw.id}>
                    {sw.name}{sw.isDefault ? ' ★' : ''}
                    {sw._count ? ` (${sw._count.images})` : ''}
                  </option>
                ))}
              </select>
            </Row>
            {slideshows.length === 0 && (
              <p className="text-[11px] text-gray-400 dark:text-gray-500">
                No slideshows yet.{' '}
                <a href="/slideshows" target="_blank" rel="noreferrer" className="text-blue-500 hover:underline">
                  Create one →
                </a>
              </p>
            )}
          </>)}
          {widget.type === 'image' && (
            <AssetPickerSection
              widgetType="image"
              assets={assets}
              selectedIds={widget.assetIds}
              onSelect={ids => onChange({ ...widget, assetIds: ids })}
            />
          )}
        </Section>
      )}

      {/* ── Clock ── */}
      {widget.type === 'clock' && (<>
        <Section title="Clock">
          <Row label="Format">
            <input type="text" value={(p.format as string) || 'HH:mm:ss'} onChange={e => setProp('format', e.target.value)}
              placeholder="HH:mm:ss" className={INPUT} />
          </Row>
          <Row label="Timezone">
            <input type="text" value={(p.timezone as string) || ''} onChange={e => setProp('timezone', e.target.value)}
              placeholder="e.g. America/Chicago" className={INPUT} />
          </Row>
        </Section>
        <Section title="Typography">
          <div className="grid grid-cols-2 gap-2">
            <Row label="Font Family">
              <select value={(p.fontFamily as string) || 'monospace'} onChange={e => setProp('fontFamily', e.target.value)} className={SELECT}>
                {FONT_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </Row>
            <Row label="Weight">
              <select value={(p.fontWeight as number) || 400} onChange={e => setProp('fontWeight', Number(e.target.value))} className={SELECT}>
                {[100,200,300,400,500,600,700,800,900].map(w => <option key={w} value={w}>{w}</option>)}
              </select>
            </Row>
            <Row label="Size (px)">
              <input type="number" value={(p.fontSize as number) || 64} onChange={e => setProp('fontSize', Number(e.target.value))} className={INPUT} />
            </Row>
            <Row label="Letter Spacing (px)">
              <input type="number" step="0.5" value={(p.letterSpacing as number) || 0} onChange={e => setProp('letterSpacing', Number(e.target.value))} className={INPUT} />
            </Row>
          </div>
          <Row label="Text Color"><input type="color" value={(p.color as string) || '#ffffff'} onChange={e => setProp('color', e.target.value)} className={COLOR} /></Row>
        </Section>
        <Section title="Background">
          <BackgroundPicker prefix="bg" p={p} setProp={setProp} allowNone label="Background" />
        </Section>
      </>)}

      {/* ── Ticker ── */}
      {widget.type === 'ticker' && (<>
        <Section title="Ticker">
          <Row label="Content">
            <textarea rows={3} value={(p.content as string) || ''} onChange={e => setProp('content', e.target.value)}
              className="w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100" />
          </Row>
          <div className="grid grid-cols-2 gap-2">
            <Row label="Scroll Speed (px/s)">
              <input type="number" min="1" value={(p.scrollSpeed as number) || 80} onChange={e => setProp('scrollSpeed', Number(e.target.value))} className={INPUT} />
            </Row>
            <Row label="Direction">
              <select value={(p.direction as string) || 'left'} onChange={e => setProp('direction', e.target.value)} className={SELECT}>
                <option value="left">← Left</option><option value="right">→ Right</option>
              </select>
            </Row>
            <Row label="Font Size (px)">
              <input type="number" value={(p.fontSize as number) || 32} onChange={e => setProp('fontSize', Number(e.target.value))} className={INPUT} />
            </Row>
            <Row label="Text Color">
              <input type="color" value={(p.color as string) || '#ffffff'} onChange={e => setProp('color', e.target.value)} className={COLOR} />
            </Row>
          </div>
        </Section>
        <Section title="Background">
          <BackgroundPicker prefix="bg" p={p} setProp={setProp} allowNone={false} label="Background" />
        </Section>
      </>)}

      {/* ── Scoreboard ── */}
      {widget.type === 'scoreboard' && (<>
        <Section title="Teams & Score">
          <Row label="Team 1"><input type="text" value={(p.team1 as string) || ''} onChange={e => setProp('team1', e.target.value)} className={INPUT} /></Row>
          <Row label="Score"><input type="text" value={(p.score as string) || '0 - 0'} onChange={e => setProp('score', e.target.value)} className={INPUT} /></Row>
          <Row label="Team 2"><input type="text" value={(p.team2 as string) || ''} onChange={e => setProp('team2', e.target.value)} className={INPUT} /></Row>
          <Row label="Font Size (px)"><input type="number" value={(p.fontSize as number) || 48} onChange={e => setProp('fontSize', Number(e.target.value))} className={INPUT} /></Row>
        </Section>
        <Section title="Colors">
          <div className="grid grid-cols-3 gap-2">
            <Row label="Team 1"><input type="color" value={(p.colorTeam1 as string) || '#ffffff'} onChange={e => setProp('colorTeam1', e.target.value)} className="h-8 w-full cursor-pointer rounded border border-gray-300 dark:border-gray-600" /></Row>
            <Row label="Score"><input type="color" value={(p.colorScore as string) || '#ffdd00'} onChange={e => setProp('colorScore', e.target.value)} className="h-8 w-full cursor-pointer rounded border border-gray-300 dark:border-gray-600" /></Row>
            <Row label="Team 2"><input type="color" value={(p.colorTeam2 as string) || '#ffffff'} onChange={e => setProp('colorTeam2', e.target.value)} className="h-8 w-full cursor-pointer rounded border border-gray-300 dark:border-gray-600" /></Row>
          </div>
        </Section>
        <Section title="Background">
          <BackgroundPicker prefix="bg" p={p} setProp={setProp} allowNone={false} label="Background" />
        </Section>
        <Section title="Hammer" defaultOpen={false}>
          <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <input type="checkbox" checked={!!p.showHammer} onChange={e => setProp('showHammer', e.target.checked)} className="h-3 w-3" /> Show Hammer
          </label>
          {p.showHammer && (
            <Row label="Hammer Team">
              <select value={(p.hammerTeam as string) || '1'} onChange={e => setProp('hammerTeam', e.target.value)} className={SELECT}>
                <option value="1">Team 1</option><option value="2">Team 2</option>
              </select>
            </Row>
          )}
        </Section>
      </>)}

      {/* ── Shared: Box Shadow + Filters ── */}
      <Section title="Shadow" defaultOpen={false}>
        <ShadowEditor p={p} setProp={setProp} />
      </Section>
      <Section title="Filters & Blend" defaultOpen={false}>
        <FilterEditor p={p} setProp={setProp} />
      </Section>
    </div>
  );
}

// ─── Drag / Resize State ──────────────────────────────────────────────────────
interface DragState   { widgetId:number; startX:number; startY:number; origX:number; origY:number; }
interface ResizeState { widgetId:number; handle:Handle; startX:number; startY:number; origX:number; origY:number; origW:number; origH:number; }

// ─── Main Editor ──────────────────────────────────────────────────────────────
export default function OverlayEditorPage() {
  const { compositionId: compositionIdParam } = useParams<{ compositionId: string }>();
  const compositionId = Number(compositionIdParam);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const containerRef = useRef<HTMLDivElement>(null);

  const [canvasScale, setCanvasScale] = useState(0.5);
  const [selectedSceneId, setSelectedSceneId] = useState<number | null>(null);
  const [selectedWidgetId, setSelectedWidgetId] = useState<number | null>(null);
  const [localWidgets, setLocalWidgets] = useState<Record<number, OverlayWidget>>({});
  const [renamingSceneId, setRenamingSceneId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [showAddWidget, setShowAddWidget] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [resizeState, setResizeState] = useState<ResizeState | null>(null);
  const [urlCopied, setUrlCopied] = useState(false);
  const [layerDragId, setLayerDragId] = useState<number | null>(null);
  const [layerDragOverId, setLayerDragOverId] = useState<number | null>(null);

  const dragRef = useRef<DragState | null>(null);
  const resizeRef = useRef<ResizeState | null>(null);
  const scaleRef = useRef(canvasScale);
  useEffect(() => { dragRef.current = dragState; }, [dragState]);
  useEffect(() => { resizeRef.current = resizeState; }, [resizeState]);
  useEffect(() => { scaleRef.current = canvasScale; }, [canvasScale]);

  // ─── Undo / Redo ─────────────────────────────────────────────────────────
  const MAX_HISTORY = 50;
  interface HistoryEntry {
    widgets: Record<number, OverlayWidget>;
    revert?: () => Promise<Record<number, OverlayWidget> | void>;
  }
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [redoStack, setRedoStack] = useState<HistoryEntry[]>([]);
  // Stable refs so undo/redo callbacks never go stale
  const localWidgetsRef = useRef<Record<number, OverlayWidget>>({});
  const historyRef = useRef<HistoryEntry[]>([]);
  const redoStackRef = useRef<HistoryEntry[]>([]);
  useEffect(() => { localWidgetsRef.current = localWidgets; }, [localWidgets]);
  useEffect(() => { historyRef.current = history; }, [history]);
  useEffect(() => { redoStackRef.current = redoStack; }, [redoStack]);

  const pushHistory = useCallback((
    snapshot: Record<number, OverlayWidget>,
    revert?: () => Promise<Record<number, OverlayWidget> | void>,
  ) => {
    const entry: HistoryEntry = { widgets: snapshot };
    if (revert) entry.revert = revert;
    setHistory(prev => [...prev.slice(-(MAX_HISTORY - 1)), entry]);
    setRedoStack([]);
  }, []);

  const handleUndo = useCallback(async () => {
    const h = historyRef.current;
    if (!h.length) return;
    const entry = h[h.length - 1]!;
    const currentSnapshot = localWidgetsRef.current;
    setHistory(prev => prev.slice(0, -1));
    setRedoStack(prev => [{ widgets: currentSnapshot }, ...prev.slice(0, MAX_HISTORY - 1)]);
    let widgetsToRestore = entry.widgets;
    if (entry.revert) {
      const result = await entry.revert();
      if (result) widgetsToRestore = result;
    }
    setLocalWidgets(widgetsToRestore);
    setHasChanges(true);
  }, []);

  const handleRedo = useCallback(async () => {
    const r = redoStackRef.current;
    if (!r.length) return;
    const entry = r[0]!;
    const currentSnapshot = localWidgetsRef.current;
    setRedoStack(prev => prev.slice(1));
    setHistory(prev => [...prev.slice(-(MAX_HISTORY - 1)), { widgets: currentSnapshot }]);
    setLocalWidgets(entry.widgets);
    setHasChanges(true);
  }, []);

  // Keyboard shortcut: Ctrl+Z = undo, Ctrl+Y / Ctrl+Shift+Z = redo
  useEffect(() => {
    const onUndoRedo = (e: KeyboardEvent) => {
      const ctrl = e.ctrlKey || e.metaKey;
      if (!ctrl) return;
      if (e.key === 'z' && !e.shiftKey) { e.preventDefault(); handleUndo(); }
      else if (e.key === 'y' || (e.key === 'z' && e.shiftKey)) { e.preventDefault(); handleRedo(); }
    };
    window.addEventListener('keydown', onUndoRedo);
    return () => window.removeEventListener('keydown', onUndoRedo);
  }, [handleUndo, handleRedo]);

  // Debounce ref for Inspector property changes (coalesce rapid edits to same widget)
  const widgetChangeWidgetIdRef = useRef<number | null>(null);
  const widgetChangeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const el = containerRef.current; if (!el) return;
    const measure = () => { const w = el.clientWidth-32, h = el.clientHeight-32; setCanvasScale(Math.min(w/CANVAS_W, h/CANVAS_H, 1)); };
    measure(); const ro = new ResizeObserver(measure); ro.observe(el); return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const sc = scaleRef.current;
      const ds = dragRef.current;
      const rs = resizeRef.current;
      if (ds) {
        const dx = (e.clientX - ds.startX) / sc, dy = (e.clientY - ds.startY) / sc;
        setLocalWidgets(prev => { const w = prev[ds.widgetId]; if (!w) return prev; return { ...prev, [ds.widgetId]: { ...w, x: Math.round(ds.origX+dx), y: Math.round(ds.origY+dy) } }; });
        setHasChanges(true);
      }
      if (rs) {
        const dx = (e.clientX - rs.startX) / sc, dy = (e.clientY - rs.startY) / sc;
        setLocalWidgets(prev => {
          const w = prev[rs.widgetId]; if (!w) return prev;
          let x=rs.origX, y=rs.origY, width=rs.origW, height=rs.origH; const h=rs.handle;
          if (h.includes('e')) width  = Math.max(20, rs.origW+dx);
          if (h.includes('s')) height = Math.max(20, rs.origH+dy);
          if (h.includes('w')) { x = rs.origX+dx; width  = Math.max(20, rs.origW-dx); }
          if (h.includes('n')) { y = rs.origY+dy; height = Math.max(20, rs.origH-dy); }
          return { ...prev, [rs.widgetId]: { ...w, x:Math.round(x), y:Math.round(y), width:Math.round(width), height:Math.round(height) } };
        });
        setHasChanges(true);
      }
    };
    const onUp = () => { setDragState(null); setResizeState(null); };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); };
  }, []);

  const { data, isLoading, error: loadError } = useQuery({
    queryKey: queryKeys.overlays.composition(compositionId),
    queryFn: () => overlaysApi.getComposition(compositionId),
    enabled: !!compositionId,
  });
  const composition: OverlayComposition | undefined = data?.composition;

  // Fetch logo assets for the image widget (uploaded via the Logos section).
  // staleTime:0 ensures newly uploaded logos appear immediately without a cache delay.
  const { data: allAssetsData } = useQuery({
    queryKey: ['assets-for-editor', composition?.facilityId, 'logo'],
    queryFn: () => assetsApi.list({ limit: 100, facilityId: composition!.facilityId, assetType: 'logo' }),
    enabled: !!composition?.facilityId,
    staleTime: 0,
  });
  const { data: slideshowsData } = useQuery({
    queryKey: ['slideshows-for-editor', composition?.facilityId],
    queryFn: () => slideshowApi.list({ facilityId: composition!.facilityId }),
    enabled: !!composition?.facilityId,
  });
  const allAssets = (allAssetsData?.assets || []).filter((a: any) => a.isActive !== false);
  const facilitySlideshows = slideshowsData?.slideshows ?? [];
  // For canvas preview: use images from the default slideshow
  const defaultSlideshow = facilitySlideshows.find(s => s.isDefault) ?? facilitySlideshows[0];
  const slideshowAssets = defaultSlideshow?.images ?? [];
  // "logo assets" for the image widget = all facility assets (any image can be placed in an image widget)
  const logoAssets = allAssets;
  // Combined map for canvas preview: assetId → { s3Url, thumbnailUrl }
  const assetMap = React.useMemo(() => {
    const map: Record<number, { s3Url: string; signedUrl?: string; thumbnailUrl?: string }> = {};
    [...slideshowAssets, ...logoAssets].forEach((a: any) => { map[a.id] = { s3Url: a.s3Url, signedUrl: a.signedUrl, thumbnailUrl: a.thumbnailUrl }; });
    return map;
  }, [slideshowAssets, logoAssets]);

  useEffect(() => {
    if (composition?.scenes?.length) { const f = composition.scenes[0]; if (f) setSelectedSceneId(prev => prev ?? f.id); }
  }, [composition]);

  const currentScene = composition?.scenes?.find(s => s.id === selectedSceneId) ?? null;

  useEffect(() => {
    if (currentScene) {
      const map: Record<number, OverlayWidget> = {};
      currentScene.widgets.forEach(w => { map[w.id] = w; });
      setLocalWidgets(map); setSelectedWidgetId(null); setHasChanges(false);
      setHistory([]); setRedoStack([]);
    }
  }, [currentScene?.id]);

  const selectedWidget = selectedWidgetId ? localWidgets[selectedWidgetId] ?? null : null;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!selectedWidgetId) return; const w = localWidgets[selectedWidgetId]; if (!w || w.isLocked) return;
      let dx=0, dy=0;
      if (e.key==='ArrowLeft') dx=-1; else if (e.key==='ArrowRight') dx=1; else if (e.key==='ArrowUp') dy=-1; else if (e.key==='ArrowDown') dy=1; else return;
      e.preventDefault();
      if (!e.repeat) pushHistory(localWidgetsRef.current);
      const step = e.shiftKey ? 10 : 1;
      setLocalWidgets(prev => { const ex=prev[selectedWidgetId]; if (!ex) return prev; return { ...prev, [selectedWidgetId]: { ...ex, x:ex.x+dx*step, y:ex.y+dy*step } }; });
      setHasChanges(true);
    };
    window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey);
  }, [selectedWidgetId, localWidgets, pushHistory]);

  const createSceneMutation = useMutation({ mutationFn: (name:string) => overlaysApi.createScene(compositionId, { name, sortOrder: composition?.scenes?.length ?? 0 }), onSuccess: (d) => { queryClient.invalidateQueries({ queryKey: queryKeys.overlays.composition(compositionId) }); setSelectedSceneId(d.scene.id); } });
  const updateSceneMutation = useMutation({ mutationFn: ({ id, name }:{id:number;name:string}) => overlaysApi.updateScene(id, { name }), onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.overlays.composition(compositionId) }) });
  const deleteSceneMutation = useMutation({ mutationFn: (id:number) => overlaysApi.deleteScene(id), onSuccess: () => { queryClient.invalidateQueries({ queryKey: queryKeys.overlays.composition(compositionId) }); setSelectedSceneId(null); } });
  const activateSceneMutation = useMutation({ mutationFn: (id:number) => overlaysApi.activateScene(id), onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.overlays.composition(compositionId) }) });
  const createWidgetMutation = useMutation({
    mutationFn: (type: OverlayWidget['type']) => overlaysApi.createWidget(selectedSceneId!, defaultWidget(type)),
    onSuccess: (d) => {
      const snapshot = localWidgetsRef.current;
      setLocalWidgets(prev => ({ ...prev, [d.widget.id]: d.widget }));
      setSelectedWidgetId(d.widget.id);
      setShowAddWidget(false);
      queryClient.invalidateQueries({ queryKey: queryKeys.overlays.composition(compositionId) });
      pushHistory(snapshot, async () => { await overlaysApi.deleteWidget(d.widget.id); return snapshot; });
    },
  });
  const deleteWidgetMutation = useMutation({
    mutationFn: (id: number) => overlaysApi.deleteWidget(id),
    onSuccess: (_d, id) => {
      const snapshot = localWidgetsRef.current;
      const deleted = snapshot[id];
      setLocalWidgets(prev => { const next = { ...prev }; delete next[id]; return next; });
      setSelectedWidgetId(null);
      queryClient.invalidateQueries({ queryKey: queryKeys.overlays.composition(compositionId) });
      if (deleted) {
        const { id: _id, createdAt: _c, updatedAt: _u, ...rest } = deleted;
        pushHistory(snapshot, async () => {
          const r = await overlaysApi.createWidget(deleted.sceneId, rest);
          const restored: Record<number, OverlayWidget> = {};
          Object.values(snapshot).forEach(w => { if (w.id !== id) restored[w.id] = w; });
          restored[r.widget.id] = r.widget;
          return restored;
        });
      }
    },
  });

  // ── Copy a widget into another overlay ──────────────────────────────────
  // Building the same lower third once per sheet — or once per club — is the
  // thing this avoids. Targets in other facilities are offered too, listed
  // after this facility's own so the common case stays first. Asset and
  // slideshow references are facility-scoped; the server re-points them on a
  // cross-facility copy and tells us what it changed, which we show.
  // ── Capture the current colours as a new scheme ─────────────────────────
  // The snapshot is taken server-side from the stored widgets, so unsaved
  // edits would not be in it. Rather than quietly capturing yesterday’s
  // colours, the button is disabled until the editor is saved.
  const [schemeModalOpen, setSchemeModalOpen] = useState(false);

  const [copySource, setCopySource] = useState<OverlayWidget | null>(null);
  const [copyTargetCompId, setCopyTargetCompId] = useState<number | null>(null);
  const [copyTargetSceneId, setCopyTargetSceneId] = useState<number | null>(null);
  const [copyBusy, setCopyBusy] = useState(false);
  const [copyResult, setCopyResult] = useState<string | null>(null);
  const [copyNotes, setCopyNotes] = useState<string[]>([]);

  const { data: copyTargetData } = useQuery({
    queryKey: ['overlay-copy-targets'],
    queryFn: () => overlaysApi.listCopyTargets(),
    enabled: !!copySource,
  });

  const copyTargets = (copyTargetData?.targets ?? []).filter(c => c.id !== compositionId);
  const facilityNames = copyTargetData?.facilityNames ?? {};

  /** Targets grouped by facility, this facility first, everything else natural-sorted. */
  const copyTargetGroups = useMemo(() => {
    const byFacility = new Map<string, typeof copyTargets>();
    for (const t of copyTargets) {
      const list = byFacility.get(t.facilityId) ?? [];
      list.push(t);
      byFacility.set(t.facilityId, list);
    }
    const own = composition?.facilityId;
    const ids = sortByNatural([...byFacility.keys()], id => facilityNames[id] ?? id);
    const ordered = own && byFacility.has(own) ? [own, ...ids.filter(id => id !== own)] : ids;
    return ordered.map(id => ({
      facilityId: id,
      label: (facilityNames[id] ?? id) + (id === own ? ' — this facility' : ''),
      items: sortByNatural(byFacility.get(id) ?? [], t => t.sheetId || t.name),
    }));
  }, [copyTargets, composition?.facilityId, facilityNames]);

  const copyTarget = copyTargets.find(c => c.id === copyTargetCompId) ?? null;
  const targetScenes = copyTarget?.scenes ?? [];
  const copyIsCrossFacility = !!copyTarget && !!composition && copyTarget.facilityId !== composition.facilityId;
  /** Does the widget hold a reference the target facility cannot resolve? */
  const copyCarriesMedia = !!copySource &&
    ((copySource.assetIds?.length ?? 0) > 0 || copySource.type === 'slideshow' ||
     copySource.properties?.['slideshowId'] !== undefined);

  const runCopy = async () => {
    if (!copySource || !copyTargetSceneId) return;
    setCopyBusy(true);
    setCopyResult(null);
    setCopyNotes([]);
    try {
      const r = await overlaysApi.copyWidgetToScene(copySource.id, copyTargetSceneId);
      const where = r.crossFacility
        ? `${facilityNames[r.targetFacilityId] ?? r.targetFacilityId} — ${r.targetOverlayName}`
        : r.targetOverlayName;
      setCopyResult(`Copied "${r.widget.name}" to ${where}.`);
      setCopyNotes(r.adjustments ?? []);
      // The target overlay's cached copy is now stale.
      queryClient.invalidateQueries({ queryKey: queryKeys.overlays.composition(r.targetCompositionId) });
    } catch (e) {
      setCopyResult(e instanceof Error ? e.message : 'Copy failed');
      setCopyNotes([]);
    } finally {
      setCopyBusy(false);
    }
  };

  const duplicateWidget = async (w: OverlayWidget) => {
    const snapshot = localWidgetsRef.current;
    const { id:_i, sceneId:_s, createdAt:_c, updatedAt:_u, ...rest } = w;
    const d = await overlaysApi.createWidget(selectedSceneId!, { ...rest, name:`${rest.name} copy`, x:rest.x+20, y:rest.y+20 });
    setLocalWidgets(prev => ({ ...prev, [d.widget.id]: d.widget }));
    setSelectedWidgetId(d.widget.id);
    queryClient.invalidateQueries({ queryKey: queryKeys.overlays.composition(compositionId) });
    pushHistory(snapshot, async () => { await overlaysApi.deleteWidget(d.widget.id); return snapshot; });
  };

  const handleSave = async () => {
    if (!currentScene) return; setIsSaving(true); setSaveError(null);
    try {
      await Promise.all(
        Object.values(localWidgets).map(w =>
          overlaysApi.updateWidget(w.id, {
            type: w.type, name: w.name,
            x: w.x, y: w.y, width: w.width, height: w.height,
            rotation: w.rotation, opacity: w.opacity,
            zIndex: w.zIndex, isVisible: w.isVisible, isLocked: w.isLocked,
            properties: w.properties, assetIds: w.assetIds,
          })
        )
      );
      setHasChanges(false);
      queryClient.invalidateQueries({ queryKey: queryKeys.overlays.composition(compositionId) });
    }
    catch (err: any) { setSaveError(err?.message || 'Save failed'); } finally { setIsSaving(false); }
  };

  const handleWidgetChange = (updated: OverlayWidget) => {
    // Push history before the first change to a widget; debounce rapid edits to the same widget
    if (widgetChangeWidgetIdRef.current !== updated.id) {
      pushHistory(localWidgetsRef.current);
    }
    if (widgetChangeTimerRef.current) clearTimeout(widgetChangeTimerRef.current);
    widgetChangeWidgetIdRef.current = updated.id;
    widgetChangeTimerRef.current = setTimeout(() => { widgetChangeWidgetIdRef.current = null; }, 600);
    setLocalWidgets(prev => ({ ...prev, [updated.id]: updated }));
    setHasChanges(true);
  };
  const startDrag = (e: React.PointerEvent, w: OverlayWidget) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    setSelectedWidgetId(w.id); // always select, even if locked
    if (w.isLocked) return;    // but don't initiate drag
    pushHistory(localWidgetsRef.current);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setDragState({ widgetId:w.id, startX:e.clientX, startY:e.clientY, origX:w.x, origY:w.y });
  };
  const startResize = (e: React.PointerEvent, w: OverlayWidget, handle: Handle) => {
    e.stopPropagation();
    pushHistory(localWidgetsRef.current);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setResizeState({ widgetId:w.id, handle, startX:e.clientX, startY:e.clientY, origX:w.x, origY:w.y, origW:w.width, origH:w.height });
  };

  if (isLoading) return <div className="flex h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-gray-400" /></div>;
  if (loadError || !composition) return (
    <div className="flex h-screen flex-col items-center justify-center gap-3">
      <AlertCircle className="h-10 w-10 text-red-400" />
      <p className="text-sm text-gray-600 dark:text-gray-400">Failed to load composition</p>
      <Button variant="outline" onClick={() => navigate('/overlays')}>Back to Overlays</Button>
    </div>
  );

  const sortedWidgets = Object.values(localWidgets).sort((a, b) => a.zIndex - b.zIndex);

  // ── Layer ordering helpers ──────────────────────────────────────────────────
  const reassignZIndices = (ordered: OverlayWidget[]) => {
    ordered.forEach((w, i) => { if (w.zIndex !== i) handleWidgetChange({ ...w, zIndex: i }); });
  };
  const bringToFront = (w: OverlayWidget) => {
    const s = [...sortedWidgets]; const idx = s.findIndex(x => x.id === w.id);
    if (idx >= s.length - 1) return;
    s.splice(idx, 1); s.push(w); reassignZIndices(s);
  };
  const sendToBack = (w: OverlayWidget) => {
    const s = [...sortedWidgets]; const idx = s.findIndex(x => x.id === w.id);
    if (idx <= 0) return;
    s.splice(idx, 1); s.unshift(w); reassignZIndices(s);
  };
  const bringForward = (w: OverlayWidget) => {
    const s = [...sortedWidgets]; const idx = s.findIndex(x => x.id === w.id);
    if (idx >= s.length - 1) return;
    const tmp = s[idx + 1] as OverlayWidget; s[idx + 1] = s[idx] as OverlayWidget; s[idx] = tmp;
    reassignZIndices(s);
  };
  const sendBackward = (w: OverlayWidget) => {
    const s = [...sortedWidgets]; const idx = s.findIndex(x => x.id === w.id);
    if (idx <= 0) return;
    const tmp = s[idx - 1] as OverlayWidget; s[idx - 1] = s[idx] as OverlayWidget; s[idx] = tmp;
    reassignZIndices(s);
  };
  const handleLayerDrop = (targetId: number) => {
    if (!layerDragId || layerDragId === targetId) { setLayerDragId(null); setLayerDragOverId(null); return; }
    const s = [...sortedWidgets];
    const fromIdx = s.findIndex(w => w.id === layerDragId);
    const toIdx = s.findIndex(w => w.id === targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const moved = s[fromIdx] as OverlayWidget; s.splice(fromIdx, 1); s.splice(toIdx, 0, moved);
    reassignZIndices(s); setLayerDragId(null); setLayerDragOverId(null);
  };
  const isLive = (id: number) => composition.activeSceneId === id;
  const outputUrl = `${window.location.origin}/overlay/${composition.facilityId}/${composition.sheetId}`;
  const varCtx: OverlayVarCtx = {
    sheetId: composition.sheetId,
    overlayName: composition.name,
    eventName: composition.eventName ?? '',
    facilityId: composition.facilityId,
  };
  const copyOutputUrl = () => {
    navigator.clipboard.writeText(outputUrl).then(() => { setUrlCopied(true); setTimeout(() => setUrlCopied(false), 2000); });
  };

  return (
    <div className="flex h-screen flex-col bg-gray-50 dark:bg-gray-950">
      <DesktopOnlyNotice message="The overlay editor uses drag, resize, and precise pointer input that don't work well on touch screens or small displays." />
      {/* Top Bar */}
      <div className="flex shrink-0 items-center justify-between border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-900">
        <div className="flex items-center gap-3">
          <Link to="/overlays" className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900 dark:hover:text-gray-100">
            <ChevronLeft className="h-4 w-4" />Overlays
          </Link>
          <span className="text-gray-300 dark:text-gray-600">/</span>
          <h1 className="text-sm font-semibold">{composition.name}</h1>
          <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs font-semibold text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">Sheet {composition.sheetId}</span>
        </div>

        {/* Browser Source URL */}
        <div className="flex items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1.5 dark:border-gray-700 dark:bg-gray-800">
          <ExternalLink className="h-3.5 w-3.5 shrink-0 text-gray-400" />
          <span className="max-w-xs truncate font-mono text-xs text-gray-500 dark:text-gray-400">{outputUrl}</span>
          <button onClick={copyOutputUrl} title="Copy browser source URL"
            className="shrink-0 rounded p-0.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            {urlCopied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
          {!composition.isPublished && (
            <span className="text-xs text-amber-500" title="Publish this composition to activate the overlay">Not published</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button onClick={handleUndo} disabled={!history.length} title="Undo (Ctrl+Z)"
            className="rounded p-1.5 text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30 dark:hover:bg-gray-800">
            <Undo2 className="h-4 w-4" />
          </button>
          <button onClick={handleRedo} disabled={!redoStack.length} title="Redo (Ctrl+Y)"
            className="rounded p-1.5 text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30 dark:hover:bg-gray-800">
            <Redo2 className="h-4 w-4" />
          </button>
          {saveError && <span className="text-xs text-red-500">{saveError}</span>}
          <button
            onClick={() => setSchemeModalOpen(true)}
            disabled={hasChanges}
            title={hasChanges
              ? 'Save your changes first — the scheme is built from the saved overlay'
              : "Create a colour scheme from this overlay's current colours"}
            className="flex items-center gap-1 rounded border border-gray-200 px-2 py-1.5 text-xs text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
            <Download className="h-3.5 w-3.5" />
            Colours as scheme
          </button>
          <Button size="sm" onClick={handleSave} disabled={isSaving || !hasChanges}>
            {isSaving ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />}
            {hasChanges ? 'Save Changes' : 'Saved'}
          </Button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* LEFT */}
        <div className="flex w-52 shrink-0 flex-col border-r border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
          <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2 dark:border-gray-800">
            <span className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Scenes</span>
            <button onClick={() => createSceneMutation.mutate(`Scene ${(composition.scenes?.length ?? 0)+1}`)} disabled={createSceneMutation.isPending}
              className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800" title="Add Scene"><Plus className="h-4 w-4" /></button>
          </div>
          <div className="max-h-40 overflow-y-auto">
            {(composition.scenes || []).map(scene => (
              <div key={scene.id} onClick={() => setSelectedSceneId(scene.id)}
                className={`group flex cursor-pointer items-center gap-1.5 px-3 py-2 text-xs ${selectedSceneId===scene.id ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300' : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800/50'}`}>
                {isLive(scene.id) && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />}
                {renamingSceneId===scene.id ? (
                  <input autoFocus value={renameValue} onChange={e => setRenameValue(e.target.value)}
                    onBlur={() => { if (renameValue.trim()) updateSceneMutation.mutate({ id:scene.id, name:renameValue.trim() }); setRenamingSceneId(null); }}
                    onKeyDown={e => { if (e.key==='Enter') { if (renameValue.trim()) updateSceneMutation.mutate({ id:scene.id, name:renameValue.trim() }); setRenamingSceneId(null); } else if (e.key==='Escape') setRenamingSceneId(null); }}
                    onClick={e => e.stopPropagation()}
                    className="flex-1 rounded border border-blue-400 bg-white px-1 py-0 text-xs dark:bg-gray-800 dark:text-gray-100" />
                ) : <span className="flex-1 truncate">{scene.name}</span>}
                <div className="ml-auto hidden items-center gap-0.5 group-hover:flex">
                  <button title="Rename" onClick={e => { e.stopPropagation(); setRenamingSceneId(scene.id); setRenameValue(scene.name); }} className="rounded p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700"><Type className="h-3 w-3" /></button>
                  <button title="Set Live" onClick={e => { e.stopPropagation(); activateSceneMutation.mutate(scene.id); }} className="rounded p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700"><Check className="h-3 w-3" /></button>
                  <button title="Delete" onClick={e => { e.stopPropagation(); if (confirm('Delete scene?')) deleteSceneMutation.mutate(scene.id); }} className="rounded p-0.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"><Trash2 className="h-3 w-3" /></button>
                </div>
              </div>
            ))}
            {!composition.scenes?.length && <p className="px-3 py-2 text-xs text-gray-400">No scenes yet</p>}
          </div>

          {currentScene && (<>
            <div className="flex items-center justify-between border-b border-t border-gray-100 px-3 py-2 dark:border-gray-800">
              <span className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Widgets</span>
              <div className="relative">
                <button onClick={() => setShowAddWidget(v => !v)} className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800" title="Add Widget"><Plus className="h-4 w-4" /></button>
                {showAddWidget && (
                  <div className="absolute right-0 top-full z-50 mt-1 w-36 rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900">
                    {WIDGET_TYPES.map(({ type, label, icon: Icon }) => (
                      <button key={type} onClick={() => createWidgetMutation.mutate(type)}
                        className="flex w-full items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800">
                        <Icon className="h-3.5 w-3.5 text-gray-500" />{label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {sortedWidgets.length === 0
                ? <p className="px-3 py-3 text-xs text-gray-400">No widgets. Click + to add.</p>
                : [...sortedWidgets].reverse().map((w, vi) => {
                    const isAtFront = vi === 0;
                    const isAtBack = vi === sortedWidgets.length - 1;
                    return (
                      <div
                        key={w.id}
                        draggable
                        onDragStart={e => { e.stopPropagation(); setLayerDragId(w.id); }}
                        onDragOver={e => { e.preventDefault(); e.stopPropagation(); setLayerDragOverId(w.id); }}
                        onDragLeave={() => setLayerDragOverId(null)}
                        onDrop={e => { e.stopPropagation(); handleLayerDrop(w.id); }}
                        onDragEnd={() => { setLayerDragId(null); setLayerDragOverId(null); }}
                        onClick={() => setSelectedWidgetId(w.id)}
                        className={`group flex cursor-pointer items-center gap-1 px-2 py-1.5 text-xs transition-colors
                          ${selectedWidgetId === w.id ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300' : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800/50'}
                          ${layerDragOverId === w.id && layerDragId !== w.id ? 'outline outline-1 outline-blue-400' : ''}
                          ${layerDragId === w.id ? 'opacity-40' : ''}
                          ${!w.isVisible ? 'opacity-40' : ''}`}
                      >
                        <GripVertical className="h-3 w-3 shrink-0 cursor-grab text-gray-300 dark:text-gray-600" />
                        <span className="flex-1 truncate">{w.name}</span>
                        <div className="flex shrink-0 items-center gap-0">
                          <button title="Bring forward" onClick={e => { e.stopPropagation(); bringForward(w); }} disabled={isAtFront} className="rounded p-0.5 text-gray-400 hover:bg-gray-200 disabled:opacity-20 dark:hover:bg-gray-700"><ChevronUp className="h-3 w-3" /></button>
                          <button title="Send backward" onClick={e => { e.stopPropagation(); sendBackward(w); }} disabled={isAtBack} className="rounded p-0.5 text-gray-400 hover:bg-gray-200 disabled:opacity-20 dark:hover:bg-gray-700"><ChevronDown className="h-3 w-3" /></button>
                          <div className="hidden items-center gap-0 group-hover:flex">
                            <button title={w.isVisible ? 'Hide' : 'Show'} onClick={e => { e.stopPropagation(); handleWidgetChange({ ...w, isVisible:!w.isVisible }); }} className="rounded p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700">{w.isVisible ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}</button>
                            <button title="Duplicate" onClick={e => { e.stopPropagation(); duplicateWidget(w); }} className="rounded p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700"><Copy className="h-3 w-3" /></button>
                            <button title="Delete" onClick={e => { e.stopPropagation(); if (confirm('Delete widget?')) deleteWidgetMutation.mutate(w.id); }} className="rounded p-0.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"><Trash2 className="h-3 w-3" /></button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
            </div>
          </>)}
        </div>

        {/* CENTER: Canvas */}
        <div ref={containerRef} className="flex flex-1 items-center justify-center overflow-hidden bg-gray-200 dark:bg-gray-800" onPointerDown={() => setSelectedWidgetId(null)}>
          {!currentScene ? (
            <div className="text-center text-sm text-gray-500 dark:text-gray-400">
              {!composition.scenes?.length ? <><p className="font-medium">No scenes yet</p><p className="mt-1 text-xs">Click + next to Scenes to add one</p></> : 'Select a scene'}
            </div>
          ) : (
            /* clip-path: inset(0) on the wrapper is the reliable way to clip BOTH visual rendering
               AND pointer-event hit-testing to the visual bounds. overflow:hidden alone does not
               reliably clip hit-testing for CSS-transformed children in Chromium. */
            <div style={{ width: CANVAS_W * canvasScale, height: CANVAS_H * canvasScale, flexShrink: 0, clipPath: 'inset(0)' }}>
              <div style={{ width:CANVAS_W, height:CANVAS_H, transform:`scale(${canvasScale})`, transformOrigin:'top left', position:'relative', overflow:'hidden', backgroundColor:'#888', backgroundImage:'linear-gradient(45deg,#666 25%,transparent 25%),linear-gradient(-45deg,#666 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#666 75%),linear-gradient(-45deg,transparent 75%,#666 75%)', backgroundSize:'24px 24px', backgroundPosition:'0 0,0 12px,12px -12px,-12px 0' }} onPointerDown={e => e.stopPropagation()}>
                {sortedWidgets.map(widget => (
                  <WidgetPreview key={widget.id} widget={widget} isSelected={selectedWidgetId===widget.id}
                    assetMap={assetMap}
                    slideshowPreviewUrl={(slideshowAssets[0] as any)?.signedUrl ?? (slideshowAssets[0] as any)?.thumbnailUrl ?? (slideshowAssets[0] as any)?.s3Url}
                    varCtx={varCtx}
                    onPointerDown={e => startDrag(e, widget)} onResizeStart={(e, h) => startResize(e, widget, h)} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: Inspector */}
        <div className="flex w-72 shrink-0 flex-col border-l border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
          <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-3 py-2 dark:border-gray-800">
            <span className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">{selectedWidget ? selectedWidget.name : 'Inspector'}</span>
            {selectedWidget && (
              <div className="flex items-center gap-1">
                <button title="Duplicate" onClick={() => duplicateWidget(selectedWidget)} className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"><Copy className="h-3.5 w-3.5" /></button>
                <button title="Copy to another overlay"
                  onClick={() => { setCopySource(selectedWidget); setCopyTargetCompId(null); setCopyTargetSceneId(null); setCopyResult(null); }}
                  className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"><ExternalLink className="h-3.5 w-3.5" /></button>
                <button title="Delete" onClick={() => { if (confirm('Delete widget?')) deleteWidgetMutation.mutate(selectedWidget.id); }} className="rounded p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            )}
          </div>
          {selectedWidget ? (
            <InspectorPanel widget={selectedWidget} onChange={handleWidgetChange}
              onBringToFront={() => bringToFront(selectedWidget)}
              onSendToBack={() => sendToBack(selectedWidget)}
              onBringForward={() => bringForward(selectedWidget)}
              onSendBackward={() => sendBackward(selectedWidget)}
              varCtx={varCtx}
              slideshows={facilitySlideshows}
              assets={
                logoAssets.map((a: any) => { const item: AssetItem = { id:a.id, originalName:a.originalName, s3Url:a.s3Url, signedUrl:a.signedUrl }; if (a.thumbnailUrl) item.thumbnailUrl=a.thumbnailUrl; return item; })
              } />
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-1 px-4 text-center text-xs text-gray-400 dark:text-gray-500">
              <p>Select a widget to edit</p>
              <p className="text-gray-300 dark:text-gray-600">Drag · Resize handles · Arrow keys · Shift+Arrow=10px</p>
            </div>
          )}
        </div>
      </div>

      <SchemeEditorModal
        open={schemeModalOpen}
        mode="from-overlay"
        source={null}
        facilityId={composition.facilityId}
        compositionId={composition.id}
        onClose={() => setSchemeModalOpen(false)}
      />

      {/* Copy a configured widget into any other overlay, this facility or another. */}
      {copySource && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
             onClick={() => setCopySource(null)}>
          <div className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl dark:bg-gray-900"
               onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Copy widget to another overlay</h3>
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              Copying <span className="font-medium text-gray-900 dark:text-gray-100">{copySource.name}</span>{' '}
              ({copySource.type}) with all of its settings — position, colours, fonts and any variable binding.
            </p>

            <div className="mt-3 space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Overlay</label>
                <select
                  value={copyTargetCompId ?? ''}
                  onChange={e => { setCopyTargetCompId(e.target.value ? Number(e.target.value) : null); setCopyTargetSceneId(null); setCopyResult(null); setCopyNotes([]); }}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100">
                  <option value="">Choose an overlay…</option>
                  {copyTargetGroups.map(g => (
                    <optgroup key={g.facilityId} label={g.label}>
                      {g.items.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.name}{c.sheetId ? ` — Sheet ${c.sheetId}` : ''}{c.isEventOverride ? ' (Event Overlay)' : ''}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {copyTargets.length === 0 && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    There are no other overlays to copy into.
                  </p>
                )}
              </div>

              {copyTargetCompId && (
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Scene</label>
                  <select
                    value={copyTargetSceneId ?? ''}
                    onChange={e => { setCopyTargetSceneId(e.target.value ? Number(e.target.value) : null); setCopyResult(null); }}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100">
                    <option value="">Choose a scene…</option>
                    {targetScenes.map(sc => (
                      <option key={sc.id} value={sc.id}>{sc.name}{sc.isDefault ? ' (default)' : ''}</option>
                    ))}
                  </select>
                  {targetScenes.length === 0 && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Loading scenes…</p>
                  )}
                </div>
              )}

              {copyIsCrossFacility && (
                <div className="rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                  Copying to another facility. The layout, colours, fonts and variable bindings
                  come across; colour roles re-resolve against that overlay's own scheme.
                  {copyCarriesMedia
                    ? ' Images and slideshows stay with their own club, so those references are' +
                      " re-pointed to the target's own media — anything it does not have is left" +
                      ' for you to pick there.'
                    : ' This widget holds no images or slideshows, so nothing needs re-pointing.'}
                </div>
              )}

              {copyResult && (
                <div className="rounded border border-blue-200 bg-blue-50 px-2 py-1.5 text-xs text-blue-900 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-200">
                  {copyResult}
                  {copyNotes.length > 0 && (
                    <ul className="mt-1 list-disc space-y-0.5 pl-4">
                      {copyNotes.map((n, i) => <li key={i}>{n}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setCopySource(null)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800">
                Close
              </button>
              <button onClick={runCopy} disabled={!copyTargetSceneId || copyBusy}
                className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                {copyBusy ? 'Copying…' : 'Copy widget'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
